import React from 'react';
import { LayoutElement, LayoutElementType, AVAILABLE_ELEMENT_TYPES } from '../index';
import { useRightSidebarConfig } from '../side-bars/right-context-sidebar';
import ElementSettingsSidebar from './ElementSettingsSidebar';
import IconProvider from '@/components/ui/IconProvider';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface ElementEditorProps {
  element: LayoutElement;
  onTypeChange: (elementId: string, newType: LayoutElementType) => void;
  onRemove: (elementId: string) => void;
  onPropsChange?: (elementId: string, newProps: Record<string, unknown>) => void;
  availableTypes: LayoutElementType[];
}

export default function ElementEditor({ 
  element, 
  onTypeChange, 
  onRemove, 
  onPropsChange,
  availableTypes 
}: ElementEditorProps) {
  const { t } = usePlatformTranslation();
  const { openCustom } = useRightSidebarConfig();

  const handleEditClick = () => {
    openCustom({
      title: `Edit ${element.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
      description: `Configure settings for ${element.id}`,
      content: (
        <ElementSettingsSidebar
          element={element}
          onTypeChange={onTypeChange}
          onPropsChange={onPropsChange || (() => {})}
          onRemove={onRemove}
          availableTypes={availableTypes}
        />
      ),
      showSaveButton: false, // We handle save/reset in the sidebar component
      showResetButton: false,
      showCloseButton: true,
    });
  };

  const handleRemove = () => {
    onRemove(element.id);
  };

  // Determine if this widget type typically has icons in the header
  const hasHeaderIcon = element.type === 'stats-widget' || element.type === 'tms-table-widget';

  return (
    <div className={`absolute top-4 ${hasHeaderIcon ? 'right-16' : 'right-4'} flex gap-2 z-30`}>
      <button
        onClick={handleEditClick}
        className="text-gray-600 hover:text-blue-600 p-2 rounded hover:bg-gray-100 transition-colors flex items-center justify-center"
        style={{ width: 36, height: 36 }}
        title={t('platform.layout.element-layout.edit_element')}
      >
        <IconProvider
          iconstring="fa-pen-to-square"
          size={16}
          color="currentColor"
        />
      </button>
      <button
        onClick={handleRemove}
        className="text-gray-600 hover:text-red-600 p-2 rounded hover:bg-gray-100 transition-colors flex items-center justify-center"
        style={{ width: 36, height: 36 }}
        title={t('platform.layout.element-layout.remove_element')}
      >
        <IconProvider
          iconstring="fa-trash"
          size={16}
          color="currentColor"
        />
      </button>
    </div>
  );
}